import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT, scrollToSection } from "@/lib/site";

const LINKS = [
    { label: "Over mij", hash: "#over-mij", testid: "nav-link-about" },
    { label: "Diensten", hash: "#diensten", testid: "nav-link-services" },
    { label: "Gratis kennismaking", hash: "#intake", testid: "nav-link-intake" },
    { label: "Contact", hash: "#contact", testid: "nav-link-contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);

    const go = (hash) => (e) => {
        e.preventDefault();
        setOpen(false);
        scrollToSection(hash);
    };

    return (
        <header className="fixed inset-x-0 top-0 z-40 border-b border-line/70 bg-sand-50/85 backdrop-blur-xl">
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
                <a
                    href="#top"
                    onClick={go("#top")}
                    data-testid="nav-brand-logo"
                    className="flex items-center gap-3"
                >
                    <Logo size={38} />
                    <span className="leading-tight">
                        <span className="block font-serif text-xl font-semibold tracking-tight">Freelance T Zorg</span>
                        <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-terracotta">
                            Begeleider • Jeugd &amp; LVB
                        </span>
                    </span>
                </a>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((link) => (
                        <a
                            key={link.hash}
                            href={link.hash}
                            onClick={go(link.hash)}
                            data-testid={link.testid}
                            className="text-sm font-medium text-moss-900/70 transition-colors duration-200 hover:text-terracotta"
                        >
                            {link.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden items-center gap-3 lg:flex">
                    <a
                        href={CONTACT.whatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="header-whatsapp-btn"
                        aria-label="WhatsApp"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white text-moss-800 transition-transform duration-200 hover:-translate-y-0.5 hover:border-[#25D366] hover:text-[#25D366]"
                    >
                        <MessageCircle className="h-4.5 w-4.5" size={18} />
                    </a>
                    <a
                        href={CONTACT.phoneHref}
                        data-testid="header-phone-btn"
                        className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 text-sm font-semibold text-moss-900 transition-transform duration-200 hover:-translate-y-0.5"
                    >
                        <Phone size={15} className="text-terracotta" />
                        {CONTACT.phoneDisplay}
                    </a>
                    <button
                        onClick={go("#intake")}
                        data-testid="header-intake-btn"
                        className="rounded-full bg-terracotta px-5 py-2.5 text-sm font-semibold text-sand-50 transition-all duration-200 hover:-translate-y-0.5 hover:bg-terracotta-dark"
                    >
                        Gratis intake
                    </button>
                </div>

                <button
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-line lg:hidden"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Menu"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-line/70 bg-sand-50/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {LINKS.map((link) => (
                                <a
                                    key={link.hash}
                                    href={link.hash}
                                    onClick={go(link.hash)}
                                    data-testid={`mobile-${link.testid}`}
                                    className="rounded-xl px-3 py-3 text-base font-medium text-moss-900/80 hover:bg-sand-100"
                                >
                                    {link.label}
                                </a>
                            ))}
                            <button
                                onClick={go("#intake")}
                                data-testid="mobile-header-intake-btn"
                                className="mt-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-sand-50"
                            >
                                Gratis intake plannen
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
